import useHttpProvider from "@/common/hooks/use-http";
import { AuthenticationProvider } from "@/common/providers/authentication";
import { notifyError, notifySuccess } from "@/components/ui/sonner";
import { HomeRoute } from "@/features/home/route";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import * as AuthenticationService from "../services/authentication";
import useActivateAccount from "./use-active-account";
import useForgotPassword from "./use-forgot-password";
import { ResetRequiredRoute } from "../route";

const FormSchema = z.object({
	email: z.string().email("Enter a valid email"),
	password: z.string({
		message: "Password is required",
	}),
});

function useLogin() {
	const { http } = useHttpProvider();
	const navigate = useNavigate();

	const location = useLocation();
	const params = new URLSearchParams(location.search);

	const sessionId = params.get("session_id");
	const from = params.get("from") || HomeRoute;

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [captchaToken, setCaptchaToken] = useState<string | undefined>();

	const {
		onResendVerificationCode,
		activateAccountForm,
		onActivateAccountSubmit,
		showActivateAccountDialog,
		setShowActivateAccountDialog,
	} = useActivateAccount();

	const {
		forgotPasswordForm,
		onForgotPasswordSubmit,
		showForgotPasswordDialog,
		setShowForgotPasswordDialog,

		resetPasswordForm,
		onResetPasswordSubmit,
		showResetPasswordDialog,
		setShowResetPasswordDialog,
	} = useForgotPassword();

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		setIsSubmitting(true);

		try {
			const response = await AuthenticationService.login(
				http,
				data.email,
				data.password,
				captchaToken
			);

			if (!response.emailVerified) {
				await AuthenticationProvider.setUser(response);
				activateAccountForm.reset();
				setShowActivateAccountDialog(true);
				return;
			}

			await AuthenticationProvider.signin(response);

			if (response.shouldResetPassword) {
				navigate(ResetRequiredRoute);
				return;
			}

			notifySuccess("Authentication", "Welcome back!");

			navigate(from || HomeRoute);
		} catch {
			notifyError(
				"Authentication.",
				"The email or password you have entered are wrong."
			);
		} finally {
			setIsSubmitting(false);
		}
	}

	async function onGoogleCredential(accessToken: string) {
		setIsSubmitting(true);
		try {
			const response = await AuthenticationService.googleSignIn(
				http,
				accessToken,
				"login"
			);
			await AuthenticationProvider.signin(response);
			notifySuccess("Authentication", "Welcome back!");
			navigate(from || HomeRoute);
		} catch (error: unknown) {
			const detail =
				typeof error === "object" && error !== null && "detail" in error
					? (error as { detail?: string }).detail
					: undefined;

			if (detail === "user_not_found") {
				notifyError(
					"No account found",
					"We couldn't find an account for that Google email. Please sign up first."
				);
			} else {
				notifyError("Google sign-in failed", "Please try again.");
			}
		} finally {
			setIsSubmitting(false);
		}
	}

	const checkForSuccessfulBoughtPlan = () => {
		if (sessionId) {
			notifySuccess(
				"Subscription bought successfully",
				"You just have bought your first subscription. You may now login."
			);
		}
	};

	useEffect(() => {
		checkForSuccessfulBoughtPlan();
	}, []);

	return {
		form,
		onSubmit,
		isSubmitting,
		captchaToken,
		setCaptchaToken,

		onResendVerificationCode,
		activateAccountForm,
		onActivateAccountSubmit,
		showActivateAccountDialog,
		setShowActivateAccountDialog,

		forgotPasswordForm,
		onForgotPasswordSubmit,
		showForgotPasswordDialog,
		setShowForgotPasswordDialog,

		resetPasswordForm,
		onResetPasswordSubmit,
		showResetPasswordDialog,
		setShowResetPasswordDialog,

		onGoogleCredential,
	};
}

export default useLogin;
